// Boxed-in banner: "path blocked" while the robot holds, "stuck" once it asks
// for help, "needs manual help" after it gives up. Driven by useBoxedIn's view.
import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PropTypes from "prop-types";
import { tokens } from "../../theme";

const TONES = {
  warning: { fg: tokens.warning, bg: tokens.warningSoft },
  danger: { fg: tokens.danger, bg: tokens.dangerSoft },
};

const BoxedInNotice = ({ view, showTarget = true, children }) => {
  if (!view) return null;
  const tone = TONES[view.tone] ?? TONES.warning;
  const Glyph = view.kind === "blocked" ? BlockIcon : WarningAmberIcon;
  const target = showTarget ? view.target : null;
  return (
    <Box
      role={view.kind === "blocked" ? "status" : "alert"}
      sx={{
        p: 1.5,
        background: tone.bg,
        border: `1px solid ${tone.fg}`,
        borderRadius: "10px",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Glyph sx={{ fontSize: 16, color: tone.fg, flex: "none" }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: tone.fg, flex: 1, minWidth: 0 }}>
          {view.title}
        </Typography>
        {view.timer && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: tone.fg,
              fontVariantNumeric: "tabular-nums",
              flex: "none",
            }}
          >
            {view.timer}
          </Typography>
        )}
      </Stack>
      {(view.detail || target) && (
        <Typography sx={{ fontSize: 12, color: tokens.text2, lineHeight: 1.5, mt: 0.5 }}>
          {view.detail}
          {target && (
            <>
              {" "}
              On the way to <strong>{target}</strong>.
            </>
          )}
        </Typography>
      )}
      {children}
    </Box>
  );
};

BoxedInNotice.propTypes = {
  view: PropTypes.shape({
    kind: PropTypes.oneOf(["blocked", "stuck", "gave_up"]).isRequired,
    tone: PropTypes.oneOf(["warning", "danger"]).isRequired,
    title: PropTypes.string.isRequired,
    detail: PropTypes.string,
    timer: PropTypes.string,
    target: PropTypes.string,
  }),
  /** Off on the trip view, which already names the destination. */
  showTarget: PropTypes.bool,
  children: PropTypes.node,
};

export default BoxedInNotice;
